import { discountQuote } from "./operations.js";
import { migrate, seed, STORAGE_KEY, snapshot } from "../../data/catalogue.js";
export const clone = (v) => structuredClone(v);
export const id = (p) =>
  p +
  "-" +
  Date.now().toString(36) +
  "-" +
  Math.random().toString(36).slice(2, 7);
export function load(storage) {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return seed();
  let old;
  try {
    old = JSON.parse(raw);
  } catch {
    throw Error(
      "Saved data could not be read. It has been retained. Restore a valid backup to continue.",
    );
  }
  if (!Array.isArray(old.products))
    throw Error("Saved data format is invalid; no records were overwritten.");
  return migrate(old);
}
export function save(storage, d) {
  storage.setItem(STORAGE_KEY, JSON.stringify(d));
  return d;
}
export const product = (d, pid) => d.products.find((p) => p.id === pid);
export const collectionName = (d, cid) =>
  d.collections.find((c) => c.id === cid)?.name || cid;
export const orderTotal = (o) =>
  Math.round(
    (o.items.reduce((n, i) => n + i.qty * i.price, 0) -
      (o.discount || 0) +
      (o.shipping || 0)) *
      100,
  ) / 100;
export const revenue = (d) =>
  d.payments
    .filter((p) => p.status === "Success")
    .reduce((n, p) => {
      const o = d.orders.find((o) => o.id === p.orderId);
      return n + (o && o.status !== "Cancelled" ? orderTotal(o) : 0);
    }, 0);
export const available = (p, s) =>
  !p.archived && p.status === "Active" && s.stock > 0;
export const stockStatus = (p) =>
  p.skus.every((s) => s.stock === 0)
    ? "Out of Stock"
    : p.skus.some((s) => s.stock <= s.threshold)
      ? "Low stock"
      : "In stock";
export function missing(p) {
  const m = [];
  if (!p.name.trim()) m.push("Product name");
  if (!p.collection) m.push("Collection");
  if (!p.description.trim()) m.push("Description");
  if (!p.skus.length) m.push("At least one SKU");
  p.skus.forEach((s, i) => {
    for (const k of [
      "code",
      "variant",
      "qty",
      "unit",
      "mrp",
      "price",
      "stock",
      "threshold",
      "weight",
      "dimensions",
    ])
      if (s[k] == null || s[k] === "") m.push(`SKU ${i + 1}: ${k}`);
  });
  return m;
}
export function validateProduct(d, p) {
  if (missing(p).length)
    throw Error("Complete operational fields: " + missing(p).join(", "));
  if (!d.collections.some((c) => c.id === p.collection && !c.archived))
    throw Error("Choose an available collection.");
  const used = new Set(
    d.products
      .filter((x) => x.id !== p.id)
      .flatMap((x) => x.skus.map((s) => s.code.toUpperCase())),
  );
  for (const s of p.skus) {
    if (!/^[A-Z0-9][A-Z0-9_-]{1,63}$/i.test(s.code))
      throw Error(
        "SKU codes must contain 2–64 letters, numbers, underscores or hyphens.",
      );
    if (used.has(s.code.toUpperCase()))
      throw Error("SKU code must be unique: " + s.code);
    used.add(s.code.toUpperCase());
    for (const k of ["qty", "weight"])
      if (!Number.isFinite(s[k]) || s[k] <= 0)
        throw Error(k + " must be a positive number.");
    for (const k of ["mrp", "price"])
      if (!Number.isFinite(s[k]) || s[k] < 0)
        throw Error(k + " must be nonnegative.");
    if (!Number.isFinite(s.discount) || s.discount < 0 || s.discount > 100)
      throw Error("Discount must be between 0 and 100%.");
    for (const k of ["stock", "threshold"])
      if (!Number.isInteger(s[k]) || s[k] < 0)
        throw Error(k + " must be a nonnegative whole number.");
    if (s.price > s.mrp) throw Error("Selling price cannot exceed MRP.");
    if (
      !/^\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?$/i.test(
        s.dimensions,
      ) ||
      s.dimensions.split(/[x×]/).some((n) => Number(n) <= 0)
    )
      throw Error(
        "Dimensions must be three positive numbers, such as 10 × 10 × 15.",
      );
  }
  if (
    p.status === "Active" &&
    !d.collections.find((c) => c.id === p.collection).launch
  )
    throw Error("Activate the collection before activating its products.");
  if (p.status === "Active" && p.skus.every((s) => s.stock === 0))
    p.status = "Out of Stock";
  return p;
}
export function adjustStock(d, pid, sid, stock, threshold, reason) {
  const p = product(d, pid),
    s = p?.skus.find((s) => s.id === sid);
  if (!s) throw Error("SKU no longer exists.");
  if (
    !Number.isInteger(stock) ||
    stock < 0 ||
    !Number.isInteger(threshold) ||
    threshold < 0
  )
    throw Error("Stock and threshold must be nonnegative whole numbers.");
  if (!reason.trim()) throw Error("An adjustment reason is required.");
  d.adjustments.push({
    id: id("adj"),
    productId: pid,
    skuId: sid,
    code: s.code,
    before: s.stock,
    after: stock,
    reason: reason.trim(),
    date: new Date().toISOString(),
  });
  s.stock = stock;
  s.threshold = threshold;
  if (p.skus.every((s) => s.stock === 0) && p.status === "Active")
    p.status = "Out of Stock";
}
export function validateCollection(d, c) {
  if (!c.name.trim() || !c.code.trim() || !c.description.trim())
    throw Error("Name, code and description are required.");
  if (!/^[A-Z0-9][A-Z0-9_-]{1,31}$/i.test(c.code))
    throw Error(
      "Collection code must be 2–32 letters, numbers, underscores or hyphens.",
    );
  if (
    d.collections.some(
      (x) =>
        x.id !== c.id &&
        (x.code.toLowerCase() === c.code.toLowerCase() ||
          x.name.toLowerCase() === c.name.toLowerCase()),
    )
  )
    throw Error(
      "Collection name and code must be unique, including archived collections.",
    );
  if (!Number.isInteger(c.order) || c.order < 1)
    throw Error("Display order must be a positive whole number.");
  return c;
}
export function validateSettings(s) {
  if (!s.legalEntity.trim() || !s.brand.trim() || !s.address.trim())
    throw Error("Business name, brand and address are required.");
  for (const k of ["businessEmail", "supportEmail"])
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s[k]))
      throw Error("Enter valid business and support email addresses.");
  for (const k of ["businessPhone", "supportPhone"])
    if (!/^\+?[0-9 ()-]{7,18}$/.test(s[k]))
      throw Error("Enter valid business and support phone numbers.");
  for (const k of ["minimumOrder", "shippingFee", "freeThreshold"])
    if (!Number.isFinite(s[k]) || s[k] < 0)
      throw Error("Order and shipping amounts must be nonnegative.");
  return s;
}
export function calculateOrder(d, lines, customerId, coupon = "", offer = "") {
  const c = d.customers.find((c) => c.id === customerId);
  if (!c) throw Error("Choose a customer.");
  if (!lines.length) throw Error("Add at least one line item.");
  const items = [],
    used = new Set();
  for (const line of lines) {
    const p = product(d, line.productId),
      s = p?.skus.find((s) => s.id === line.skuId);
    if (!p || !s || !available(p, s))
      throw Error("Item is unavailable for ordering.");
    if (!Number.isInteger(line.qty) || line.qty < 1 || line.qty > s.stock)
      throw Error("Quantity must be a whole number within available stock.");
    if (used.has(s.id))
      throw Error("Combine duplicate SKU lines before calculating.");
    used.add(s.id);
    items.push(snapshot(p, s, line.qty));
  }
  const subtotal = items.reduce((n, i) => n + i.price * i.qty, 0),
    s = d.settings;
  if (subtotal < s.minimumOrder)
    throw Error(`Minimum merchandise value is ₹${s.minimumOrder}.`);
  const { discount, applied } = discountQuote(
    d,
    items,
    customerId,
    coupon,
    offer,
  );
  const shipping = subtotal > s.freeThreshold ? 0 : s.shippingFee;
  return {
    items,
    subtotal,
    discount,
    shipping,
    total: Math.round((subtotal - discount + shipping) * 100) / 100,
    coupon: applied,
  };
}
export function createOrder(d, lines, customerId, coupon = "", offer = "") {
  const quote = calculateOrder(d, lines, customerId, coupon, offer);
  const nextNumber =
      Math.max(
        1000,
        ...d.orders.map((o) =>
          /^PO-\d+$/.test(o.id) ? Number(o.id.slice(3)) : 0,
        ),
      ) + 1,
    oid = "PO-" + nextNumber,
    pid = id("TX");
  for (const i of quote.items) {
    const p = product(d, i.productId),
      s = p.skus.find((s) => s.id === i.skuId);
    adjustStock(
      d,
      p.id,
      s.id,
      s.stock - i.qty,
      s.threshold,
      "Reserved for " + oid,
    );
  }
  const o = {
    id: oid,
    customerId,
    date: new Date().toISOString(),
    items: quote.items,
    discount: quote.discount,
    shipping: quote.shipping,
    coupon: quote.coupon,
    status: "Pending",
    paymentId: pid,
    carrier: "",
    tracking: "",
    note: "",
    stockReserved: true,
    cancelRestored: false,
    settingsSnapshot: clone(d.settings),
    exampleRecord: true,
  };
  d.orders.push(o);
  d.payments.push({
    id: pid,
    orderId: oid,
    status: "Pending",
    method: "Online",
    reference: "",
    date: o.date,
    exampleRecord: true,
  });
  return o;
}
export const transitions = {
  Pending: ["Processing", "Cancelled"],
  Processing: ["Dispatched", "Cancelled"],
  Dispatched: ["Delivered"],
  Delivered: [],
  Cancelled: [],
};
export function transitionOrder(d, oid, next, carrier = "", tracking = "") {
  const o = d.orders.find((o) => o.id === oid);
  if (!o || !transitions[o.status].includes(next))
    throw Error("Choose a valid next fulfilment status.");
  if (next === "Dispatched") {
    if (d.payments.find((p) => p.id === o.paymentId)?.status !== "Success")
      throw Error("Successful payment is required before dispatch.");
    if (!carrier.trim() || !tracking.trim())
      throw Error("Courier and tracking number are required.");
  }
  if (next === "Cancelled" && o.stockReserved && !o.cancelRestored) {
    for (const i of o.items) {
      const p = product(d, i.productId),
        s = p?.skus.find((s) => s.id === i.skuId);
      if (s)
        adjustStock(
          d,
          p.id,
          s.id,
          s.stock + i.qty,
          s.threshold,
          "Cancellation " + oid,
        );
    }
    o.cancelRestored = true;
  }
  o.status = next;
  if (next === "Dispatched") {
    o.carrier = carrier.trim();
    o.tracking = tracking.trim();
  }
  return o;
}
export function notices(d) {
  return [
    {
      id: "payment-pending",
      title: "Payments awaiting confirmation",
      body: `${d.payments.filter((p) => p.status === "Pending").length} transactions awaiting a payment record update.`,
      route: "payments",
      count: d.payments.filter((p) => p.status === "Pending").length,
    },
    {
      id: "low-stock",
      title: "Low-stock products",
      body: `${d.products.filter((p) => !p.archived && p.skus.some((s) => s.stock <= s.threshold)).length} products at or below their stock threshold.`,
      route: "products",
      count: d.products.filter(
        (p) => !p.archived && p.skus.some((s) => s.stock <= s.threshold),
      ).length,
    },
    {
      id: "banners",
      title: "Banner content review",
      body: `${d.banners.filter((b) => !b.approved).length} banner drafts awaiting review.`,
      route: "notifications",
      count: 0,
    },
    {
      id: "orders-processing",
      title: "Orders to process",
      body: `${d.orders.filter((o) => o.status === "Pending").length} new orders awaiting processing.`,
      route: "orders",
      count: d.orders.filter((o) => o.status === "Pending").length,
    },
  ]
    .filter((n) => n.count > 0)
    .map((n) => ({ ...n, signature: n.id + ":" + n.count }));
}
